import { z } from 'zod';
import { safeHref } from './blocks.ts';

/**
 * Rich text (TipTap JSON) restricted to a safe, fixed vocabulary. The editor can only produce these nodes, and the API re-validates
 * them, so stored content can never carry scripts, raw HTML, event handlers or unsafe links. Rendering walks this tree into elements.
 */
export const richMarkSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('bold') }),
  z.object({ type: z.literal('italic') }),
  z.object({ type: z.literal('code') }),
  z.object({ type: z.literal('link'), attrs: z.object({ href: safeHref }) }),
]);
export type RichMark = z.infer<typeof richMarkSchema>;

export type RichNode =
  | { type: 'text'; text: string; marks?: RichMark[] }
  | { type: 'hardBreak' }
  | { type: 'horizontalRule' }
  | { type: 'paragraph' | 'blockquote' | 'bulletList' | 'orderedList' | 'listItem' | 'codeBlock'; content?: RichNode[] }
  | { type: 'heading'; attrs: { level: 2 | 3 }; content?: RichNode[] };

const MAX_DEPTH = 6;
const MAX_NODES = 4000;
const nodeSchema: z.ZodType<RichNode> = z.lazy(() =>
  z.union([
    z.object({ type: z.literal('text'), text: z.string().max(5000), marks: z.array(richMarkSchema).max(4).optional() }),
    z.object({ type: z.literal('hardBreak') }),
    z.object({ type: z.literal('horizontalRule') }),
    z.object({ type: z.enum(['paragraph', 'blockquote', 'bulletList', 'orderedList', 'listItem', 'codeBlock']), content: z.array(nodeSchema).max(200).optional() }),
    z.object({ type: z.literal('heading'), attrs: z.object({ level: z.union([z.literal(2), z.literal(3)]) }), content: z.array(nodeSchema).max(50).optional() }),
  ]),
);

export const richDocSchema = z
  .object({ type: z.literal('doc'), content: z.array(nodeSchema).max(200).default([]) })
  .superRefine((doc, ctx) => {
    let nodes = 0;
    const walk = (n: RichNode, depth: number) => {
      nodes += 1;
      if (depth > MAX_DEPTH) { ctx.addIssue({ code: 'custom', message: 'Content is nested too deeply' }); return; }
      if ('content' in n && n.content) n.content.forEach((c) => walk(c, depth + 1));
    };
    doc.content.forEach((n) => walk(n, 1));
    if (nodes > MAX_NODES) ctx.addIssue({ code: 'custom', message: 'Content is too long' });
  });
export type RichDoc = z.infer<typeof richDocSchema>;

export const emptyDoc = (): RichDoc => ({ type: 'doc', content: [] });
export const docFromText = (text: string): RichDoc => ({ type: 'doc', content: text.split(/\n{2,}/).filter(Boolean).map((t) => ({ type: 'paragraph', content: [{ type: 'text', text: t }] })) });

/** Plain text of a document, for summaries, emptiness checks, reading time and placeholder detection. */
export function richToText(doc: RichDoc | RichNode | undefined): string {
  if (!doc) return '';
  const out: string[] = [];
  const walk = (n: RichNode | RichDoc) => {
    if (n.type === 'text') out.push(n.text);
    else if (n.type === 'hardBreak') out.push('\n');
    else if ('content' in n && n.content) { n.content.forEach((c) => walk(c)); if (['paragraph', 'heading', 'listItem', 'blockquote', 'codeBlock'].includes(n.type)) out.push('\n'); }
  };
  walk(doc);
  return out.join('').replace(/\n{3,}/g, '\n\n').trim();
}
export const isEmptyDoc = (doc: RichDoc | undefined): boolean => richToText(doc).length === 0;
export const wordCount = (doc: RichDoc): number => richToText(doc).split(/\s+/).filter(Boolean).length;
